import { HttpHelper, HttpServiceParams } from 'rbh-merchant-core';
import Auth from '@aws-amplify/auth';
import { envConfig } from '../config';
import { CognitoIdToken } from 'amazon-cognito-identity-js';
import { ACCESS_TOKEN_HEADER, AUTH_LOCAL_STORAGE_KEY, COGNITO_CHALLENGE, COGNITO_RESPONSE } from './auth-constant';
import Axios from 'axios';
import { generalCreators } from '../redux/general/general-action';
import { GENERAL_STATE_KEY } from '../redux/general/general-reducer';
import { MODAL_TYPE } from '../redux/general/general-constant';
import { AUTH_STATE_KEY } from './auth-reducer';
import { authCreators } from './auth-action';
import { store } from '../redux/store';
import { clearLocalStorage, registerLocalStorage, getLocalStorage } from '../shared/helpers/utility';

class AuthHelperClass {
  tokenExpirationInterceptorId: any;
  tokenExpireTime: Date | undefined;
  maintenance: any;
  configure = () => {
    Auth.configure({
      Auth: {
        mandatorySignIn: true,
        region: envConfig.awsCognitoRegion,
        userPoolId: envConfig.awsCognitoUserPoolId,
        userPoolWebClientId: envConfig.awsCognitoUserPoolWebClientId
      }
    });
  }

  getCurrentSession = async () => {
    try {
      const user = await Auth.currentSession();
      return { user, success: true };
    } catch (e) {
      if (e === 'No current user' || e?.code === COGNITO_RESPONSE.NOT_AUTHORIZED_EXCEPTION) {
        return { error: e, success: false };
      }
      return e;
    }
  }

  forgotPassword = async (params: HttpServiceParams) => {
    try {
      const { data } = params;
      await Auth.forgotPassword(data.merchantUserId);
      return { merchantUserId: data.merchantUserId, success: true };
    } catch (e) {
      return e;
    }
  }

  forgotPasswordSubmit = async (params: HttpServiceParams) => {
    try {
      const { data } = params;
      await Auth.forgotPasswordSubmit(data.merchantUserId, data.ConfirmationCode, data.Password);
      return { success: true };
    } catch (e) {
      return e;
    }
  }

  completeNewPassword = async (params: HttpServiceParams) => {
    try {
      const { data } = params;
      await Auth.completeNewPassword(
        data.cognitoUser,
        data.newPassword,
        {});
      return { success: true };
    } catch (e) {
      return e;
    }
  }

  toLowerCase(email: string) {
    try {
      return email.toLowerCase();
    } catch (e) {
      console.error(e);
    }
    return email;
  }

  login = async (params: HttpServiceParams) => {
    try {
      const { data } = params;
      data.email = this.toLowerCase(data.email);
      const user = await Auth.signIn(data.email, data.password);
      if (user) {
        registerLocalStorage(AUTH_LOCAL_STORAGE_KEY.CLIENT_ID, user.pool.clientId);
        registerLocalStorage(AUTH_LOCAL_STORAGE_KEY.TOKEN_ID, 'login');
        registerLocalStorage(AUTH_LOCAL_STORAGE_KEY.IS_REMEMBER, data.remember);
        registerLocalStorage(AUTH_LOCAL_STORAGE_KEY.EMAIL, data.email);
      }
      return user;
    } catch (e) {
      return e;
    }
  }

  getRole = async () => {
    try {
      const userInfo = await Auth.currentUserInfo();
      return userInfo?.attributes?.['custom:merchantRole'];
    } catch (e) {
      return e;
    }
  }

  logOut = async () => {
    try {
      this.clearAuthLocalStorage();
      HttpHelper.setHeader(ACCESS_TOKEN_HEADER, '');
      AuthHelper.tokenExpireTime = undefined;
      store.dispatch(authCreators.setAuthState(AUTH_STATE_KEY.ID_TOKEN, undefined));
      store.dispatch(authCreators.setAuthState(AUTH_STATE_KEY.STATUS, COGNITO_CHALLENGE.LOGOUT_SUCCESS));
      store.dispatch(authCreators.setAuthState(AUTH_STATE_KEY.EMAIL, undefined));
      await Auth.signOut();
      return { success: true };
    } catch (error) {
      return error;
    }
  }

  setTokenExpireTime = (token: CognitoIdToken) => {
    this.tokenExpireTime = new Date(token.payload.exp * 1000);
    registerLocalStorage(AUTH_LOCAL_STORAGE_KEY.EXPIRE_TIME, this.tokenExpireTime.getTime());
  }

  createTokenExpirationInterceptor = () => {
    HttpHelper.getInstance()?.interceptors.request.eject(this.tokenExpirationInterceptorId); // NOSONAR [typescript:S905] will support optional chaining in SonarQube 8.5
    HttpHelper.getInstance()?.interceptors.response.eject(this.maintenance); // NOSONAR [typescript:S905] will support optional chaining in SonarQube 8.5
    this.tokenExpirationInterceptorId = HttpHelper.getInstance()?.interceptors.request.use(async (config: any) => {
      // Check if token is going to expire soon..
      if (this.tokenExpireTime && (this.tokenExpireTime.getTime() - new Date().getTime() < 0)
        && config.headers[ACCESS_TOKEN_HEADER]) {
        const { user } = await this.getCurrentSession();
        if (user) {
          HttpHelper.setHeader(ACCESS_TOKEN_HEADER, user.getIdToken().getJwtToken());
          config.headers[ACCESS_TOKEN_HEADER] = user.getIdToken().getJwtToken();
          this.setTokenExpireTime(user.getIdToken());
        } else {
          await this.logOut();
          store.dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
            visible: true, onOk: () => {
            }, type: MODAL_TYPE.ERROR
          }));
          throw new Axios.Cancel('Request rejected from unable to renew access token...');
        }
      }
      return config;
    });

    this.maintenance = HttpHelper.getInstance()?.interceptors.response.use(async (response: any) => {
      return response;
    }
      , (error: any) => {
        if (error.response && error.response.status === 403 && error.response.data.message === 'Under Maintenance') {
          store.dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.IS_MAINTENANCE, true));
        }
        return Promise.reject(error);
      });

  }

  clearAuthLocalStorage = () => {
    Object.values(AUTH_LOCAL_STORAGE_KEY).forEach((key) => {
      clearLocalStorage(key);
    });
  }

  getBusinessTypeId = () => {
    return Number(getLocalStorage(AUTH_LOCAL_STORAGE_KEY.BUSINESS_TYPE_ID));
  }

  getMerchantTypeId = () => {
    return Number(getLocalStorage(AUTH_LOCAL_STORAGE_KEY.MERCHANT_TYPE_ID));
  }
}

export const AuthHelper = new AuthHelperClass();
